import type { FunctionComponent } from 'react';
import { ViewIds } from '@ardoq/api-types';
import { OBJECT_CONTEXT_MENU_NAME } from '@ardoq/context-menu';
import { updateViewProperties } from '@ardoq/view-settings';
import { Features, featureOperations } from '@ardoq/features';
import { connect, dispatchAction } from '@ardoq/rxbeach';
import { combineLatest, distinctUntilChanged, map, shareReplay } from 'rxjs';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { getErrorInfoBoxLabels } from 'locales/getErrorInfoBoxLabels';
import { isDiscoverJourney$ } from 'canvas/isDiscoverJourney';
import { isPresentationMode } from 'env';
import { featureContext$ } from 'streams/featureContext$';
import {
  createViewStatusReporter,
  registerWhileSubscribed,
  withDataWork,
} from 'tabview/activeViewsStatus/viewStatusReporter';
import { toViewpointModeMainViewInstanceId } from 'appLayout/mainViewInstanceIds';
import { getDefaultViewSettings } from 'views/environmentals/viewSettings/getDefaultViewSettings';
import { getViewpointModeViewSettingsWithChanges$ } from 'views/environmentals/viewSettings/viewpointModeViewSettings$';
import { createViewpointModeDependencyMapViewCommands } from '../commands';
import { DependencyMapView } from '../DependencyMapView';
import type { DependencyMapViewProps } from '../types';
import {
  getViewModel$,
  toViewpointModeViewModel,
  viewModelRoutines,
} from '../viewModel$';
import {
  toViewpointModeSettingsControlsDependencies,
  type ViewpointModeDependencyMapSettingsControlsDependencies,
} from './settingsControls';

const VIEW_ID = ViewIds.DEPENDENCY_MAP_2;
const VIEW_INSTANCE_ID = toViewpointModeMainViewInstanceId(VIEW_ID);

const publishSettingsControlsData = (
  dependencies: ViewpointModeDependencyMapSettingsControlsDependencies
) => {
  dispatchAction(
    updateViewProperties({
      viewId: VIEW_ID,
      viewProperties: dependencies,
    })
  );
};

const viewpointModeSettingsControls = {
  getSettingsControlsData: toViewpointModeSettingsControlsDependencies,
  publishSettingsControlsData,
};

const dependencyMapCommands$ = combineLatest([
  isDiscoverJourney$,
  featureContext$,
]).pipe(
  map(
    ([isDiscoverJourney, featureContext]) =>
      isDiscoverJourney ||
      featureOperations.hasFeature(featureContext, Features.CANVAS_PROTOTYPE)
  ),
  distinctUntilChanged(),
  map(isQuickExploreSelectionEnabled =>
    createViewpointModeDependencyMapViewCommands({
      isPresentationMode: isPresentationMode(),
      isQuickExploreSelectionEnabled,
    })
  ),
  shareReplay({ bufferSize: 1, refCount: true })
);

const viewStatusReporter = createViewStatusReporter(VIEW_INSTANCE_ID);

const [coreViewModel$, interactiveStates$] = getViewModel$({
  viewInstanceId: VIEW_INSTANCE_ID,
  viewSettingsWithChanges$: getViewpointModeViewSettingsWithChanges$(
    VIEW_ID,
    VIEW_INSTANCE_ID
  ),
  viewStatusReporter,
  defaultViewSettings: getDefaultViewSettings(VIEW_ID, true),
});

const viewModel$ = combineLatest([
  coreViewModel$.pipe(
    map(toViewpointModeViewModel),
    withDataWork(viewStatusReporter, 'source')
  ),
  interactiveStates$,
  i18nLocale$,
  dependencyMapCommands$,
]).pipe(
  map(
    ([
      viewModel,
      interactiveStates,
      i18nLocale,
      commands,
    ]): DependencyMapViewProps => ({
      ...viewModel,
      ...interactiveStates,
      i18nLocale,
      errorInfoBoxLabels: getErrorInfoBoxLabels(i18nLocale),
      commands,
      contextMenuName: OBJECT_CONTEXT_MENU_NAME,
      viewStatusReporter,
    })
  ),
  registerWhileSubscribed(viewStatusReporter),
  shareReplay({ bufferSize: 1, refCount: true })
);

export const ViewpointModeDependencyMapView: FunctionComponent = connect(
  DependencyMapView,
  viewModel$,
  viewModelRoutines(viewModel$, viewpointModeSettingsControls)
);
